import { inject, Injectable } from '@angular/core';
import { IIncidencePlatformApiService } from './incidence-platform-api.interface';
import { map, Observable } from 'rxjs';
import {
  CreateIncidencePlatform,
  DamageAreaPlatform,
  DamageSeverityPlatform,
  DamageTypePlatform,
  IncidencePlatform,
} from '../domain/incidence-platform.model';
import { environment } from '@env/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ResponseApi } from '@domain/shared/infrastructure/models/response-api.interface';
import {
  mapDamageAreaPlatformApiModelsToDamageAreaPlatforms,
  mapDamageSeverityPlatformApiModelsToDamageSeverityPlatforms,
  mapDamageTypePlatformApiModelsToDamageTypePlatforms,
  mapIncidencePlatformApiModelsToIncidencePlatforms,
  mapIncidencePlatformApiModelToIncidencePlatform,
} from '../mappers/incidence-platform.mappers';

@Injectable({
  providedIn: 'root',
})
export class IncidencePlatformApiService
  implements IIncidencePlatformApiService
{
  private readonly _baseUrl = environment.baseUrl;
  private _http = inject(HttpClient);

  getIncidencePlatform(queryParams?: {
    id?: number;
    automobileId?: number;
  }): Observable<IncidencePlatform | IncidencePlatform[]> {
    const url = `${this._baseUrl}incidence-platforms?id=${
      queryParams?.id ?? ''
    }&automobileId=${queryParams?.automobileId ?? ''}`;
    const headers = new HttpHeaders();
    return this._http.get<ResponseApi>(url, { headers }).pipe(
      map((response) => {
        if (Array.isArray(response.results)) {
          return mapIncidencePlatformApiModelsToIncidencePlatforms(
            response.results
          );
        }
        return mapIncidencePlatformApiModelToIncidencePlatform(
          response.results
        );
      })
    );
  }

  createIncidencePlatform(
    data: CreateIncidencePlatform
  ): Observable<IncidencePlatform> {
    const url = `${this._baseUrl}incidence-platforms`;
    const headers = new HttpHeaders();
    return this._http
      .post<ResponseApi>(url, data, { headers })
      .pipe(
        map((response) =>
          mapIncidencePlatformApiModelToIncidencePlatform(response.results)
        )
      );
  }

  deleteIncidencePlatform(id: number): Observable<boolean> {
    const url = `${this._baseUrl}incidence-platforms/${id}`;
    const headers = new HttpHeaders();
    return this._http
      .delete<ResponseApi>(url, { headers })
      .pipe(map((response) => response.ok));
  }

  getDamagesAreaPlatform(): Observable<DamageAreaPlatform[]> {
    const url = `${this._baseUrl}damages-area-platforms`;
    const headers = new HttpHeaders();
    return this._http
      .get<ResponseApi>(url, { headers })
      .pipe(
        map((response) =>
          mapDamageAreaPlatformApiModelsToDamageAreaPlatforms(response.results)
        )
      );
  }

  getDamagesTypePlatform(): Observable<DamageTypePlatform[]> {
    const url = `${this._baseUrl}damages-type-platforms`;
    const headers = new HttpHeaders();
    return this._http
      .get<ResponseApi>(url, { headers })
      .pipe(
        map((response) =>
          mapDamageTypePlatformApiModelsToDamageTypePlatforms(response.results)
        )
      );
  }

  getDamagesSeverityPlatform(): Observable<DamageSeverityPlatform[]> {
    const url = `${this._baseUrl}damages-severity-platforms`;
    const headers = new HttpHeaders();
    return this._http
      .get<ResponseApi>(url, { headers })
      .pipe(
        map((response) =>
          mapDamageSeverityPlatformApiModelsToDamageSeverityPlatforms(
            response.results
          )
        )
      );
  }
}
